import React, { useMemo } from 'react';
import style from './style.module.css';
import classNames from 'classnames';
import { Typography } from '@material-ui/core';
import { StatusService } from 'external-services/status-service';
import {
  bookingStatusType,
  BookingType,
  IBookingStatus,
  IPaymentDetailsBase,
  StatusCharacterType,
} from 'shared-types/index';
import { IStatusSymbol } from './types';
// import {
//   getBookingStatusColor,
//   getColorForStatus,
// } from 'design-v2/common/BookingStatusList/bookingStatusColorService';
import StatusConfigService from 'external-services/status-config-service';
import LocationService from 'external-services/location-service';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

const NS = 'StatusSymbol';

/**
 * Need to check for Windows OS because fonts render differently than on Mac OS.
 * The "$" on windows, due to different font size, needed slight translateY adjustment,
 * but was ok without it on Mac.
 */
const isWindowsOS = LocationService.isWindows();

export const StatusTitleRender = ({
  status,
  paymentDetails,
  bookingType,
  isTitle = true,
  isHeader = false,
}: {
  status: IBookingStatus;
  paymentDetails: IPaymentDetailsBase;
  bookingType: BookingType;
  isTitle?: boolean;
  isHeader?: boolean;
}) => {
  const { isSBPaymentPending, isSBPaymentUnconfirmed } = useMemo(() => {
    return StatusService.getStandbyPaymentPendingState(
      status.statusType,
      paymentDetails,
      bookingType
    );
  }, [status, paymentDetails, bookingType]);
  return (
    <Typography
      variant="body1"
      className={classNames({
        [style.statusName]: true,
        [style.statusNameForTitle]: isTitle,
        [style.statusNameForHeader]: isHeader,
      })}
    >
      {status.name}

      {isSBPaymentUnconfirmed && <span className={style.smallNameText}> (SB)</span>}

      {isSBPaymentPending && <span className={style.smallNameText}> ($)</span>}
    </Typography>
  );
};

export default function StatusSymbol({
  status,
  bookingType,
  paymentDetails,
  isModal = false,
  isTitle = false,
  customStyle = {},
  showDropdownIcon,
  isNarrowVersion = false,
}: IStatusSymbol) {
  if (!status) {
    return null;
  }
  const isPaymentPending = status?.statusType === bookingStatusType?.pendingPayment;
  const isSeated = status?.statusType === bookingStatusType?.seated;
  const isOrdered = status?.name?.toLowerCase() === 'ordered';

  const { char1, char2 } = useMemo(() => {
    return StatusService.getStatusCodeCharacters(status, paymentDetails, bookingType);
  }, [status, paymentDetails, bookingType]);

  const isSimpleCode = char1.text && !char2.text;

  const statusStyle = useMemo(() => {
    return StatusConfigService.getStatusColorBasedOnStatusType(
      status?.statusType,
      isSeated,
      isOrdered,
      isPaymentPending,
      status?.statusColor
    );
  }, [status?.statusType, isSeated, isOrdered, isPaymentPending, status?.statusColor]);

  return (
    <div
      className={classNames({
        [style.root]: true,
        [style.modalRoot]: isModal,
        [style.titleRoot]: isTitle,
        [style.isWindows]: isWindowsOS,
        [style.isDropdownIcon]: showDropdownIcon,
        [style.reducedWidth]: isNarrowVersion,
      })}
      aria-label="Booking Status"
      data-ns="status-symbol"
      style={{ ...statusStyle, ...customStyle }}
    >
      <span className={style.statusText} data-testid="characterWrap">
        {char1.text && (
          <span
            className={classNames({
              [style.largeStatusSymbolA]:
                !isSimpleCode && char1.large && char1.type === StatusCharacterType.typeA,
              [style.largeStatusSymbolB]:
                !isSimpleCode && char1.large && char1.type === StatusCharacterType.typeB,
            })}
          >
            {char1.text}
          </span>
        )}
        {char2.text && (
          <span
            className={classNames({
              [style.largeStatusSymbolA]: char2.large && char2.type === StatusCharacterType.typeA,
              [style.largeStatusSymbolB]: char2.large && char2.type === StatusCharacterType.typeB,
            })}
          >
            {char2.text}
          </span>
        )}
        {showDropdownIcon && <ArrowDropDownIcon fontSize={'small'} style={{fontSize: '1.8rem'}} />}
      </span>
    </div>
  );
}
